import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../utils/api';
import { memberPortalBranding } from '../../config/memberPortalBranding';

export default function MemberLogin() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();
  const landingBackground =
    'radial-gradient(circle at 12% 18%, rgba(0,107,63,0.26), transparent 40%), radial-gradient(circle at 90% 86%, rgba(196,30,58,0.28), transparent 36%), linear-gradient(135deg, #f7f9f8 0%, #e9f3ee 20%, #b8d8c8 40%, #0b5e35 62%, #7a1f28 82%, #151515 100%)';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loginData = tab === 0 
        ? { email, password }
        : { phone, password };

      const response = await api.post('/member-portal/login', loginData);
      
      if (response.data.success) {
        localStorage.setItem('memberToken', response.data.token);
        localStorage.setItem('member', JSON.stringify(response.data.member));
        navigate('/member-portal');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 3, md: 5 },
        background: landingBackground,
      }}
    >
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <GroupsIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
              {memberPortalBranding.associationName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {memberPortalBranding.tagline}
            </Typography>
          </Box>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Email" />
          <Tab label="Phone" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {tab === 0 ? (
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              autoComplete="email"
            />
          ) : (
            <TextField
              fullWidth
              label="Phone Number"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              margin="normal"
              autoComplete="tel"
            />
          )}

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.5 }}>
            <Link to="/member-portal/forgot-password" style={{ textDecoration: 'none' }}>
              <Button variant="text" size="small">
                Forgot password?
              </Button>
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Login'}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
            Don't have an account?{' '}
            <Link to="/member-portal/register" style={{ textDecoration: 'none' }}>
              <Button variant="text" size="small">
                Register here
              </Button>
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Are you an administrator?{' '}
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Button variant="text" size="small">
                Admin Portal
              </Button>
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
    </Box>
  );
}
